export const TREE_RUNTIME_DISCREPANCY_LOADED_COHORT_KEYS =
  Object.freeze([
    'canonicalAdditionLoadedParentIdsCount',
    'canonicalAdditionLoadedRelationshipWorkflowCount',
    'legacyGenerationMissingLoadedReciprocalSpouseIdCount',
    'legacyGenerationMissingLoadedNonreciprocalSpouseIdCount',
  ] as const);

export type TreeRuntimeDiscrepancyLoadedCohortKey =
  typeof TREE_RUNTIME_DISCREPANCY_LOADED_COHORT_KEYS[number];

export type TreeRuntimeDiscrepancyLoadedCohortEvidence =
  Readonly<
    Record<
      TreeRuntimeDiscrepancyLoadedCohortKey,
      number
    >
  >;

export const TREE_RUNTIME_MISSING_PARENT_REFERENCE_LIFECYCLE_KEYS =
  Object.freeze([
    'missingParentReferenceActiveSameGraphCount',
    'missingParentReferenceSoftDeletedSameGraphCount',
    'missingParentReferenceCrossGraphCount',
    'missingParentReferencePhysicallyAbsentCount',
    'missingParentReferenceIndeterminateCount',
  ] as const);

export type TreeRuntimeMissingParentReferenceLifecycleKey =
  typeof TREE_RUNTIME_MISSING_PARENT_REFERENCE_LIFECYCLE_KEYS[number];

export type TreeRuntimeMissingParentReferenceLifecycleEvidence =
  Readonly<
    Record<
      TreeRuntimeMissingParentReferenceLifecycleKey,
      number
    >
  >;

export const TREE_RUNTIME_DISCREPANCY_COHORT_LIFECYCLE_KEYS =
  Object.freeze([
    ...TREE_RUNTIME_DISCREPANCY_LOADED_COHORT_KEYS,
    ...TREE_RUNTIME_MISSING_PARENT_REFERENCE_LIFECYCLE_KEYS,
  ] as const);

export type TreeRuntimeDiscrepancyCohortLifecycleKey =
  typeof TREE_RUNTIME_DISCREPANCY_COHORT_LIFECYCLE_KEYS[number];

export type TreeRuntimeDiscrepancyCohortLifecycleEvidence =
  Readonly<
    Record<
      TreeRuntimeDiscrepancyCohortLifecycleKey,
      number
    >
  >;

const sanitizeCountRecord = <
  Key extends string,
>(
  candidate: unknown,
  keys: readonly Key[],
): Readonly<Record<Key, number>> | null => {
  if (
    !candidate ||
    typeof candidate !== 'object'
  ) {
    return null;
  }

  const value =
    candidate as Record<string, unknown>;

  const sanitized =
    Object.fromEntries(
      keys.map(
        key => [key, 0],
      ),
    ) as Record<Key, number>;

  for (const key of keys) {
    const count = value[key];

    if (
      typeof count !== 'number' ||
      !Number.isInteger(count) ||
      count < 0 ||
      count > 1_000_000
    ) {
      return null;
    }

    sanitized[key] = count;
  }

  return Object.freeze(sanitized);
};

export const sanitizeTreeRuntimeDiscrepancyLoadedCohortEvidence =
  (
    candidate: unknown,
  ): TreeRuntimeDiscrepancyLoadedCohortEvidence | null =>
    sanitizeCountRecord(
      candidate,
      TREE_RUNTIME_DISCREPANCY_LOADED_COHORT_KEYS,
    );

export const sanitizeTreeRuntimeMissingParentReferenceLifecycleEvidence =
  (
    candidate: unknown,
  ): TreeRuntimeMissingParentReferenceLifecycleEvidence | null =>
    sanitizeCountRecord(
      candidate,
      TREE_RUNTIME_MISSING_PARENT_REFERENCE_LIFECYCLE_KEYS,
    );

export const sanitizeTreeRuntimeDiscrepancyCohortLifecycleEvidence =
  (
    candidate: unknown,
  ): TreeRuntimeDiscrepancyCohortLifecycleEvidence | null =>
    sanitizeCountRecord(
      candidate,
      TREE_RUNTIME_DISCREPANCY_COHORT_LIFECYCLE_KEYS,
    );

export const createEmptyTreeRuntimeDiscrepancyLoadedCohortEvidence =
  (): TreeRuntimeDiscrepancyLoadedCohortEvidence =>
    Object.freeze(
      Object.fromEntries(
        TREE_RUNTIME_DISCREPANCY_LOADED_COHORT_KEYS.map(
          key => [key, 0],
        ),
      ) as Record<
        TreeRuntimeDiscrepancyLoadedCohortKey,
        number
      >,
    );

export const createEmptyTreeRuntimeMissingParentReferenceLifecycleEvidence =
  (): TreeRuntimeMissingParentReferenceLifecycleEvidence =>
    Object.freeze(
      Object.fromEntries(
        TREE_RUNTIME_MISSING_PARENT_REFERENCE_LIFECYCLE_KEYS.map(
          key => [key, 0],
        ),
      ) as Record<
        TreeRuntimeMissingParentReferenceLifecycleKey,
        number
      >,
    );

export const createEmptyTreeRuntimeDiscrepancyCohortLifecycleEvidence =
  (): TreeRuntimeDiscrepancyCohortLifecycleEvidence =>
    Object.freeze({
      ...createEmptyTreeRuntimeDiscrepancyLoadedCohortEvidence(),
      ...createEmptyTreeRuntimeMissingParentReferenceLifecycleEvidence(),
    });

export const composeTreeRuntimeDiscrepancyCohortLifecycleEvidence =
  (input: {
    readonly loadedCohorts:
      TreeRuntimeDiscrepancyLoadedCohortEvidence;

    readonly missingParentReferenceLifecycle:
      TreeRuntimeMissingParentReferenceLifecycleEvidence;
  }): TreeRuntimeDiscrepancyCohortLifecycleEvidence =>
    Object.freeze({
      ...input.loadedCohorts,
      ...input.missingParentReferenceLifecycle,
    });
